/**
 * OutcomeCatalogSection — Project Detail's "Outcome Catalog" collapsible
 * section: add form, table, and edit/delete. Extracted from
 * `routes/projects/$projectId.tsx` (issue #1411).
 */
import { useState } from 'react';
import {
  createProjectOutcome,
  ApiError,
  type CreateProjectOutcomeInput,
} from '../../lib/apiClient';
import { ProjectOutcomeStatus, type ProjectOutcome } from '../../graphql/types';
import { StatusBadge, PermissionGate, CollapsibleSection } from '../shared';

const ForgeButton = 'forge-button' as React.ElementType;
const IconElement = 'forge-icon' as React.ElementType;
const BannerElement = 'forge-banner' as React.ElementType;

export interface OutcomeCatalogSectionProps {
  projectId: string;
  outcomes: ProjectOutcome[];
  outcomeInUse: (oc: ProjectOutcome) => boolean;
  canManageProject: boolean;
  onOutcomeAdded: () => void;
  onEditOutcome: (outcome: ProjectOutcome) => void;
  onRequestDeleteOutcome: (outcome: ProjectOutcome) => void;
}

/** Add-outcome form + catalog table for the Project Detail page. */
export function OutcomeCatalogSection({
  projectId,
  outcomes,
  outcomeInUse,
  canManageProject,
  onOutcomeAdded,
  onEditOutcome,
  onRequestDeleteOutcome,
}: OutcomeCatalogSectionProps): JSX.Element {
  const [showOutcomeForm, setShowOutcomeForm] = useState(false);
  const [outcomeForm, setOutcomeForm] = useState({ name: '', description: '', displayOrder: '' });
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddOutcome = async (): Promise<void> => {
    if (!outcomeForm.name.trim()) {
      setFormError('Outcome name is required');
      return;
    }
    setIsSubmitting(true);
    setFormError(null);
    try {
      const input: CreateProjectOutcomeInput = {
        projectId,
        name: outcomeForm.name.trim(),
        description: outcomeForm.description || undefined,
        displayOrder: Number(outcomeForm.displayOrder) || outcomes.length + 1,
        status: ProjectOutcomeStatus.Active,
      };
      await createProjectOutcome(input);
      setOutcomeForm({ name: '', description: '', displayOrder: '' });
      setShowOutcomeForm(false);
      onOutcomeAdded();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Failed to add outcome');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="project-detail-section" data-testid="outcome-catalog-section">
      <CollapsibleSection
        headingId="outcomes-heading"
        title="Outcome Catalog"
        testId="outcome-catalog"
        actions={
          <PermissionGate permission="project:update">
            <ForgeButton
              type="button"
              variant="outlined"
              onClick={() => {
                setShowOutcomeForm(true);
                setFormError(null);
              }}
            >
              Add Outcome
            </ForgeButton>
          </PermissionGate>
        }
      >
        {formError && showOutcomeForm && (
          <BannerElement theme="error" dismissed={false} role="alert">
            <span>{formError}</span>
          </BannerElement>
        )}

        {showOutcomeForm && (
          <div className="inline-form" data-testid="outcome-form">
            <div className="inline-form-field">
              <label className="forge-typography--caption" htmlFor="oc-name">
                Name *
              </label>
              <input
                id="oc-name"
                className="search-input"
                value={outcomeForm.name}
                onChange={(e) => setOutcomeForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="Outcome name"
                aria-label="Outcome name"
              />
            </div>
            <div className="inline-form-field">
              <label className="forge-typography--caption" htmlFor="oc-desc">
                Description
              </label>
              <input
                id="oc-desc"
                className="search-input"
                value={outcomeForm.description}
                onChange={(e) => setOutcomeForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Optional description"
                aria-label="Outcome description"
              />
            </div>
            <ForgeButton
              type="button"
              variant="raised"
              disabled={isSubmitting}
              onClick={() => void handleAddOutcome()}
            >
              Save
            </ForgeButton>
            <ForgeButton type="button" variant="outlined" onClick={() => setShowOutcomeForm(false)}>
              Cancel
            </ForgeButton>
          </div>
        )}

        {outcomes.length === 0 && !showOutcomeForm && (
          <p className="forge-typography--body2 text-secondary">No outcomes defined yet.</p>
        )}
        {outcomes.length > 0 && (
          <table className="project-detail-table" aria-label="Outcome catalog">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Status</th>
                <th scope="col">Description</th>
                {canManageProject && (
                  <th scope="col" className="project-detail-table-col-actions">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {outcomes.map((oc) => {
                const inUse = outcomeInUse(oc);
                return (
                  <tr key={oc.entityId} data-testid={`outcome-${oc.entityId}`}>
                    <td className="forge-typography--body2">{oc.name}</td>
                    <td>
                      <StatusBadge value={oc.status} variant="lifecycle" />
                    </td>
                    <td className="forge-typography--caption text-secondary">
                      {oc.description ?? '—'}
                    </td>
                    {canManageProject && (
                      <td className="project-detail-table-actions">
                        <ForgeButton
                          type="button"
                          variant="text"
                          onClick={() => onEditOutcome(oc)}
                          aria-label={`Edit outcome ${oc.name}`}
                          data-testid={`edit-outcome-${oc.entityId}`}
                        >
                          <IconElement name="edit" slot="start" aria-hidden="true" />
                          Edit
                        </ForgeButton>
                        <ForgeButton
                          type="button"
                          variant="text"
                          className="action-btn--danger"
                          disabled={inUse}
                          onClick={() => onRequestDeleteOutcome(oc)}
                          aria-label={`Delete outcome ${oc.name}`}
                          title={
                            inUse
                              ? 'Cannot delete: outcome is assigned to one or more services'
                              : undefined
                          }
                          data-testid={`delete-outcome-${oc.entityId}`}
                        >
                          <IconElement name="delete" slot="start" aria-hidden="true" />
                          Delete
                        </ForgeButton>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CollapsibleSection>
    </section>
  );
}
